import { useCallback, useEffect, useState } from 'react';
import {
  PENDING_STEPS_EVENT,
  acknowledgePendingSteps,
  listPendingSteps,
  type PendingSteps,
} from './pending-steps';

/**
 * Subscribes to the persisted checklist. Separate from
 * `PendingStepsBanner` so that file exports only components (fast
 * refresh), and separate from `pending-steps.ts` so that module stays
 * React-free and directly unit-testable.
 */
export function usePendingSteps(): [
  PendingSteps[],
  (siteId: string, action: PendingSteps['action']) => void,
] {
  const [entries, setEntries] = useState<PendingSteps[]>(() => listPendingSteps());

  useEffect(() => {
    const refresh = () => setEntries(listPendingSteps());
    window.addEventListener(PENDING_STEPS_EVENT, refresh);
    // Cross-tab: two admin tabs open is normal (one commissioning, one
    // watching the Sites table), and an acknowledgement in one should
    // not leave a phantom banner in the other.
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(PENDING_STEPS_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  const acknowledge = useCallback((siteId: string, action: PendingSteps['action']) => {
    acknowledgePendingSteps(siteId, action);
    setEntries(listPendingSteps());
  }, []);

  return [entries, acknowledge];
}
